import { AccountBioFields } from '../../constants';
import {
  AccountLifecycleEventProperty,
  AccountOrigin,
  AccountStatus,
  ChangeRequestStatus,
  SortOption,
} from '../global';
import { ApiFile } from './file.type';
import { LeadSortOption } from './lead.type';
import { Link } from './link.type';
import { Theme } from './theme.type';
import { Unit } from './unit.type';

export type Account = {
  id: string;
  uuid: string;
  username?: string;
  firstName?: string | null;
  lastName?: string | null;
  theme?: Theme | null;
  email?: string | null;
  workEmail?: string | null;
  workEmail2?: string | null;
  workUrl?: string | null;
  bioText?: string | null;
  workPhone?: string | null;
  mobilePhone?: string | null;
  homePhone?: string | null;
  hotlinePhone?: string | null;
  assistantPhone?: string | null;
  assistantEmail?: string | null;
  assistantName?: string | null;
  assistantGeneric?: string | null;
  workFax?: string | null;
  files?: Array<ApiFile>;
  links?: Array<Link>;
  profileImageUrl?: string | null;
  profileImageState?: AccountImageState;
  position?: string | null;
  academicDegree?: string | null;
  academicDegree2?: string | null;
  role?: string | null;
  units?: Array<Unit> | null; ///Team and units are the same
  namePrefix?: string | null;
  nameSuffix?: string | null;
  salutation?: string | null;
  division?: string | null;
  company?: string | null;
  logoHeaderUrl: string | null;
  logoHeaderState?: AccountImageState;
  address1_addressee?: string | null;
  address1_addressLine1?: string | null;
  address1_addressLine2?: string | null;
  address1_postCode?: string | null;
  address1_city?: string | null;
  address1_country?: string | null;
  address1_label?: string | null;
  address2_addressee?: string | null;
  address2_addressLine1?: string | null;
  address2_addressLine2?: string | null;
  address2_postCode?: string | null;
  address2_city?: string | null;
  address2_country?: string | null;
  address2_label?: string | null;
  defaultLinkId: number | null;
  preferredLang?: string | null;
  bannerImageUrl?: string | null;
  bannerImageState?: AccountImageState;
  addressTemplate1Id?: number | null;
  isThemeAdmin?: boolean;
  isUnitAdmin?: boolean;
  addressTemplate2Id?: number | null;
  authRoles?: Array<AuthRole> | null;
  origin?: AccountOrigin | null;
  status: AccountStatus;
  actionPreferences?: ActionPreferences;
  accountMeta?: AccountMeta | null;
  attachedThemeId?: number | null;
  createdOn?: Date;
  accountToUnits?: AccountToUnits[] | null;
  authEmail?: string | null;
  costCenter?: string | null;
  internalNotes?: string | null;
  autoVCardDownload?: boolean;
  showTeamDirectoryBox: boolean;
  defaultBioFields?: Array<keyof AccountBioFields> | null;
};

export type AccountToUnits = {
  id?: number;
  accountId: string;
  account?: Account;
  unitsId: number;
  unit?: Unit;
  isMain: boolean;
};

export type AuthRole = {
  id?: string;
  name: string;
  niceName: string;
};

export type ActionPreferences = {
  dismissedFreeTrialBannerAt?: number;
  linkCode_dontShowConfirmationDialog?: boolean;
  linkCode_Filters?: {
    selectedColumn: keyof Account;
    orderBy: keyof Account;
    sort?: 'asc' | 'desc';
    withoutNfc?: boolean;
  };
  myLeads_SortOptions?: LeadSortOption;
  mdLeads_options?: LeadSortOption;
  mdLeads_filters?: string[];
  leads_filters?: string[];
  myCards_reconnectCardAlertVisible?: boolean;
  mdUnits_options?: SortOption;
  leads_addEditPreferredButtonAction?: 'save' | 'enrich';
  alert_newFeatureEnrichmentDismissed?: boolean;
};
export const ACTION_PREFERENCE_KEYS: Array<keyof ActionPreferences> = [
  'dismissedFreeTrialBannerAt',
];

export type AccountMeta = {
  onboardingCategory: Array<{ key: string; lang?: string; value?: string }>;
  onboardingReferrer: Array<{ key: string; lang?: string; value?: string }>;
  onboardingCompanySize: Array<{ key: string }>;
  potentialUsers: Array<{ key: string }>;
  usageGoal: Array<{ key: string; lang?: string; value?: string }>;
  useCasePreference: Array<{ key: string; lang?: string; value?: string }>;
};

type Serializable<T> = {
  [K in keyof T as T[K] extends string | boolean | number ? K : never]: T[K];
};
type ExcludeForeignKeys<T> = {
  [K in keyof T as K extends `${string}Id` ? never : K]: T[K];
};
type SerializableAndExcludeForeignKeys<T> = Serializable<T> & ExcludeForeignKeys<T>;

type AccountExcludedDefaults = Pick<
  Account,
  | 'id'
  | 'username'
  | 'files'
  | 'links'
  | 'theme'
  | 'units'
  | 'authRoles'
  | 'origin'
  | 'status'
  | 'actionPreferences'
  | 'accountMeta'
  | 'createdOn'
>;

type AccountIncludedDefaults = Pick<Account, 'addressTemplate1Id' | 'addressTemplate2Id'>;

// export type AccountDefaultsConfig = Omit<ExcludeForeignKeys<Account> & Serializable<Account>, keyof AccountExcludedDefaults>;
export type AccountDefaultsConfig = Partial<
  Omit<
    {
      [K in keyof SerializableAndExcludeForeignKeys<Account>]: Account[K];
    },
    keyof AccountExcludedDefaults
  > &
    AccountIncludedDefaults
>;

// account onboarding types
export type OnboardingDataProps1 = {
  firstName?: string | null;
  lastName?: string | null;
};

export type OnboardingDataProps2 = {
  position?: string | null;
  role?: string | null;
  title?: string | null;
  company?: string | null;
  workUrl?: string | null;
  mobilePhone?: string | null;
  workPhone?: string | null;
  email?: string | null;
  workEmail?: string | null;
  division?: string | null;
  address1_addressee?: string | null;
  address1_addressLine1?: string | null;
  address1_addressLine2?: string | null;
  address1_postCode?: string | null;
  address1_city?: string | null;
  address1_country?: string | null;
} & OnboardingDataProps1;

export enum AccountImageState {
  DEFINED = 'defined',
  DEFAULT = 'default',
  DELETED = 'deleted',
}

export type AccountChangeRequest = {
  id: number;
  accountId: string;
  account: Account;
  status: ChangeRequestStatus;
  property: AccountLifecycleEventProperty;
  newValue?: { value: any };
  createdOn: Date;
  action: 'create' | 'update' | 'delete';
  linkId?: number;
  fileId?: number;
};
